// Self-healing lazy loader for exceljs.
//
// exceljs is code-split into its own on-demand chunk so it stays out of the
// initial bundle (it only loads when an operator clicks "Export to Excel").
// When a new frontend build is deployed, `s3 sync --delete` removes the
// previous build's chunks. A browser tab still running the OLD build then 404s
// on the now-deleted chunk and webpack throws a ChunkLoadError
// ("Loading chunk NNN failed"). Rather than surface that dead-end error, we
// detect it and reload once to pick up the current build, after which the
// import succeeds.
//
// A sessionStorage guard ensures we reload at most once per stale-tab episode,
// so a genuinely-unavailable chunk can't trigger an infinite reload loop.

const RELOAD_GUARD_KEY = 'exceljs-chunk-stale-reload';

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: string }).name;
  const message = (err as { message?: string }).message ?? '';
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk \S+ failed/i.test(message)
  );
}

/**
 * Dynamically import exceljs, self-healing across frontend deploys.
 * Returns the ExcelJS default export (the `new ExcelJS.Workbook()` factory).
 */
export async function loadExcelJS() {
  try {
    const mod = await import('exceljs');
    // Loaded cleanly — clear any prior stale-reload marker so a future
    // genuine failure can still trigger its one reload.
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      /* sessionStorage may be unavailable (private mode) — non-fatal */
    }
    return mod.default;
  } catch (err) {
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
    } catch {
      /* ignore */
    }

    if (isChunkLoadError(err) && !alreadyReloaded) {
      // Stale tab across a deploy: reload once to fetch the current build,
      // then block this export flow so the reload takes over.
      try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
      } catch {
        /* ignore */
      }
      window.location.reload();
      return new Promise<never>(() => {});
    }

    throw err;
  }
}
